import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Circle, Truck, PackageCheck, Package, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Booked", "In Transit", "Out for Delivery", "Delivered"];

const fmt = (v) => {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return String(v).slice(0, 16); }
};

export default function TrackShipment() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/track/${token}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);

  const stepIndex = data ? STEPS.indexOf(data.delivery_status) : -1;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="track-page">
      <div className="max-w-lg mx-auto">
        <div className="bg-[#17365D] text-white rounded-t-lg px-6 py-5">
          <div className="text-lg font-bold tracking-wide">HI-TECH SILICO</div>
          <div className="text-xs text-blue-200 uppercase tracking-widest mt-0.5">Shipment Tracking</div>
        </div>

        {error && (
          <div className="bg-white rounded-b-lg px-6 py-10 text-center text-slate-500" data-testid="track-notfound">
            Shipment not found. Please check your tracking link.
          </div>
        )}

        {data && (
          <div className="bg-white rounded-b-lg px-6 py-6 space-y-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Order</div>
                <div className="font-mono font-semibold text-slate-800" data-testid="track-order">{data.order_number || "-"}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-slate-500">LR / Docket</div>
                <div className="font-mono font-semibold text-slate-800">{data.lr_number || "-"}</div>
              </div>
            </div>

            <div className={`rounded-md px-4 py-3 text-sm font-semibold ${data.delivery_status === "Delivered" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-blue-50 text-blue-800 border border-blue-200"}`} data-testid="track-status">
              {data.delivery_status === "Delivered" ? <PackageCheck size={16} className="inline mr-2 -mt-0.5" /> : <Truck size={16} className="inline mr-2 -mt-0.5" />}
              {data.delivery_status === "Pending" ? "Dispatch being prepared" : data.delivery_status}
            </div>

            <div className="flex items-center justify-between px-1">
              {STEPS.map((s, i) => (
                <div key={s} className="flex-1 flex flex-col items-center relative">
                  {i > 0 && <div className={`absolute top-2.5 right-1/2 w-full h-0.5 ${i <= stepIndex ? "bg-emerald-500" : "bg-slate-200"}`} style={{ zIndex: 0 }} />}
                  <div className="relative z-10 bg-white">
                    {i <= stepIndex
                      ? <CheckCircle2 size={20} className="text-emerald-500" />
                      : <Circle size={20} className="text-slate-300" />}
                  </div>
                  <div className={`text-[10px] mt-1.5 text-center leading-tight ${i <= stepIndex ? "text-slate-800 font-medium" : "text-slate-400"}`}>{s}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Transporter</div>
                <div className="text-slate-800">{data.transporter || "-"}</div>
              </div>
              <div className="bg-slate-50 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Route</div>
                <div className="text-slate-800 flex items-center gap-1"><MapPin size={12} className="text-slate-400" />{data.from_pincode || "-"} → {data.to_pincode || "-"}</div>
                {data.distance_km > 0 && <div className="text-xs text-slate-500 mt-0.5">≈ {data.distance_km} km</div>}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Journey</div>
              <div className="border-l-2 border-blue-200 pl-4 space-y-4">
                {(data.status_history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="relative">
                    <div className={`absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full border-2 border-white ${i === 0 ? "bg-blue-600" : "bg-slate-300"}`} />
                    <div className={`text-sm ${i === 0 ? "font-semibold text-slate-900" : "text-slate-700"}`}>{h.status}</div>
                    <div className="text-xs text-slate-500">{fmt(h.at)}</div>
                  </div>
                ))}
                {!data.status_history?.length && <div className="text-sm text-slate-500 flex items-center gap-2"><Package size={14} /> Awaiting first update</div>}
              </div>
            </div>

            <div className="text-center text-[11px] text-slate-400 border-t border-slate-100 pt-4">
              Hi-Tech Silico · Manufacturers of Premium Silicone Products<br />
              Questions? Call +91 8888837157 or email info@hitechsilico.com
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
